const LOCK_CLASS = 'custom-inspiration-lock';
const SUPPORTS_CLOSED_BY = typeof HTMLDialogElement !== 'undefined' && 'closedBy' in HTMLDialogElement.prototype;

class CustomInspirationGrid extends HTMLElement {
  #abortController = null;
  #trigger = null;
  #pushedHistory = false;
  #shareUrl = '';
  #prev = null;
  #next = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.sheet = this.querySelector('.custom-inspiration-sheet');
    this.status = this.querySelector('[data-custom-inspiration-status]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const checkedFilter = this.querySelector('.custom-inspiration__chip-input:checked');
    if (checkedFilter) {
      this.#setFilter(checkedFilter.value);
    }

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('.custom-inspiration__chip-input')) {
        this.#onFilterChange(event.target);
      }
    }, { signal });

    this.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('.custom-inspiration-card__trigger') : null;
      if (trigger && this.contains(trigger)) {
        this.open(trigger);
      }
    }, { signal });

    if (!this.sheet) {
      return;
    }

    this.sheet.addEventListener('click', (event) => {
      const clickedClose = event.target instanceof Element && event.target.closest('[data-custom-inspiration-close]');
      const clickedBackdrop = !SUPPORTS_CLOSED_BY && event.target === this.sheet;
      if (clickedClose || clickedBackdrop) {
        this.sheet.close();
      }
    }, { signal });

    this.sheet.addEventListener('close', () => this.#onSheetClose(), { signal });
    this.sheet.querySelector('[data-custom-inspiration-share]')?.addEventListener('click', () => this.#share(), { signal });

    this.#prev = this.sheet.querySelector('[data-custom-inspiration-prev]');
    this.#next = this.sheet.querySelector('[data-custom-inspiration-next]');
    this.#prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.#next?.addEventListener('click', () => this.#step(1), { signal });
    this.querySelector('[data-custom-inspiration-random]')?.addEventListener('click', () => this.#openRandom(), { signal });

    this.sheet.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.target instanceof Element && event.target.closest('video, input, textarea, select')) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        this.#step(-1);
      } else if (event.key === 'ArrowRight') {
        this.#step(1);
      }
    }, { signal });

    if (this.hasAttribute('data-links')) {
      window.addEventListener('popstate', () => this.#syncWithHash(), { signal });
      this.#syncWithHash();
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    document.documentElement.classList.remove(LOCK_CLASS);
  }

  open(trigger, { updateHistory = true } = {}) {
    const card = trigger?.closest('.custom-inspiration-card');
    if (!card || !this.sheet) {
      return;
    }

    const wasOpen = this.sheet.open;

    this.#trigger = trigger;
    this.#fillSheet(card, wasOpen);

    if (!wasOpen) {
      this.sheet.showModal();
      document.documentElement.classList.add(LOCK_CLASS);
    }

    this.sheet.querySelector('.custom-inspiration-sheet__scroll')?.scrollTo({ top: 0 });

    const handle = card.dataset.idea;
    if (updateHistory && handle && this.hasAttribute('data-links') && this.#hashHandle() !== handle) {
      if (wasOpen && this.#pushedHistory) {
        history.replaceState(history.state, '', `#${encodeURIComponent(handle)}`);
      } else {
        history.pushState(history.state, '', `#${encodeURIComponent(handle)}`);
        this.#pushedHistory = true;
      }
    }
  }

  #items() {
    const items = Array.from(this.querySelectorAll('.custom-inspiration__item'));
    const filter = this.getAttribute('data-filter');
    return filter ? items.filter((item) => item.dataset.category === filter) : items;
  }

  #step(delta) {
    const items = this.#items();
    const target = items[items.indexOf(this.#trigger?.closest('.custom-inspiration__item')) + delta];
    this.#openItem(target);
  }

  #openRandom() {
    const current = this.#trigger?.closest('.custom-inspiration__item');
    const items = this.#items().filter((item) => item !== current);
    this.#openItem(items[Math.floor(Math.random() * items.length)]);
  }

  #openItem(item) {
    const trigger = item?.querySelector('.custom-inspiration-card__trigger');
    if (trigger) {
      this.open(trigger);
    }
  }

  #fillSheet(card, announce) {
    const title = card.querySelector('.custom-inspiration-card__trigger')?.textContent.trim() ?? '';
    const categoryName = card.dataset.categoryName ?? '';

    const titleElement = this.sheet.querySelector('[data-custom-inspiration-title]');
    const categoryElement = this.sheet.querySelector('[data-custom-inspiration-category]');
    const mediaElement = this.sheet.querySelector('[data-custom-inspiration-media]');
    const descriptionElement = this.sheet.querySelector('[data-custom-inspiration-description]');
    const extrasElement = this.sheet.querySelector('[data-custom-inspiration-extras]');

    titleElement.textContent = title;
    categoryElement.textContent = categoryName;
    categoryElement.hidden = !categoryName;
    this.sheet.dataset.category = card.closest('[data-category]')?.dataset.category ?? '';

    const videoTemplate = card.querySelector('template.custom-inspiration-card__video');
    const image = card.querySelector('img.custom-inspiration-card__image');

    if (videoTemplate) {
      mediaElement.replaceChildren(videoTemplate.content.cloneNode(true));
    } else if (image) {
      const sheetImage = image.cloneNode();
      sheetImage.className = 'custom-inspiration-sheet__image';
      sheetImage.removeAttribute('fetchpriority');
      sheetImage.loading = 'eager';
      sheetImage.sizes = '(max-width: 699px) calc(100vw - 32px), 496px';
      sheetImage.alt = card.dataset.imageAlt ?? '';
      mediaElement.replaceChildren(sheetImage);
    } else {
      mediaElement.replaceChildren();
    }

    const description = card.querySelector('.custom-inspiration-card__description');
    descriptionElement.replaceChildren(...(description ? Array.from(description.childNodes, (node) => node.cloneNode(true)) : []));

    const products = card.querySelector('.custom-inspiration-card__products');
    extrasElement.replaceChildren(...(products ? [products.cloneNode(true)] : []));

    const handle = card.dataset.idea;
    this.#shareUrl = this.hasAttribute('data-links') && handle ? `${location.origin}${location.pathname}#${encodeURIComponent(handle)}` : '';

    const canShare = Boolean(this.#shareUrl) && (typeof navigator.share === 'function' || typeof navigator.clipboard?.writeText === 'function');
    const shareButton = this.sheet.querySelector('[data-custom-inspiration-share]');
    const footer = this.sheet.querySelector('.custom-inspiration-sheet__footer');

    if (shareButton) {
      shareButton.hidden = !canShare;
    }

    if (footer) {
      footer.hidden = !canShare && !this.#prev;
    }

    if (this.#prev) {
      const items = this.#items();
      const index = items.indexOf(card.closest('.custom-inspiration__item'));

      this.#prev.disabled = index <= 0;

      if (this.#next) {
        this.#next.disabled = index === -1 || index >= items.length - 1;
      }

      const position = this.sheet.querySelector('[data-custom-inspiration-position]');
      if (position) {
        position.textContent = `${index + 1} / ${items.length}`;
      }
    }

    const sheetStatus = this.sheet.querySelector('[data-custom-inspiration-sheet-status]');
    if (sheetStatus) {
      sheetStatus.textContent = announce ? title : '';
    }

    this.#setShareStatus('');
  }

  #onSheetClose() {
    document.documentElement.classList.remove(LOCK_CLASS);
    this.sheet.querySelectorAll('video').forEach((video) => video.pause());

    if (this.#pushedHistory) {
      this.#pushedHistory = false;
      history.back();
    } else if (this.hasAttribute('data-links') && this.#hashHandle()) {
      history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    }

    const trigger = this.#trigger;
    this.#trigger = null;

    if (trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
  }

  #syncWithHash() {
    const handle = this.#hashHandle();
    const card = handle ? Array.from(this.querySelectorAll('.custom-inspiration-card')).find((element) => element.dataset.idea === handle) : null;

    if (card) {
      if (!this.sheet.open) {
        this.open(card.querySelector('.custom-inspiration-card__trigger'), { updateHistory: false });
      }
    } else if (this.sheet.open) {
      this.#pushedHistory = false;
      this.sheet.close();
    }
  }

  #hashHandle() {
    try {
      return decodeURIComponent(location.hash.slice(1));
    } catch {
      return '';
    }
  }

  #onFilterChange(input) {
    this.#setFilter(input.value);

    const grid = this.querySelector('.custom-inspiration__grid');
    if (grid) {
      if (!this.reducedMotion.matches) {
        grid.animate({ opacity: [0.4, 1] }, { duration: 150, easing: 'ease-out' });
      }

      if (grid.getBoundingClientRect().top < 0) {
        grid.scrollIntoView({ block: 'start' });
      }
    }

    if (this.status) {
      this.status.textContent = input.dataset.announcement ?? '';
    }

    const url = new URL(location.href);
    if (input.value === 'all') {
      url.searchParams.delete('category');
    } else {
      url.searchParams.set('category', input.value);
    }
    history.replaceState(history.state, '', url);
  }

  #setFilter(value) {
    if (value === 'all') {
      this.removeAttribute('data-filter');
    } else {
      this.setAttribute('data-filter', value);
    }
  }

  async #share() {
    if (!this.#shareUrl) {
      return;
    }

    const title = this.sheet.querySelector('[data-custom-inspiration-title]')?.textContent ?? document.title;

    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title, url: this.#shareUrl });
      } else {
        await navigator.clipboard.writeText(this.#shareUrl);
        this.#setShareStatus(this.dataset.copiedLabel ?? '');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        this.#setShareStatus('');
      }
    }
  }

  #setShareStatus(message) {
    const status = this.sheet?.querySelector('[data-custom-inspiration-share-status]');
    if (status) {
      status.textContent = message;
    }
  }

}

if (!customElements.get('custom-inspiration-grid')) {
  customElements.define('custom-inspiration-grid', CustomInspirationGrid);
}
